import { Alert, flash } from "@xola/ui-kit";
import { AxiosResponse } from "axios";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    Affiliate,
    LineItemTemplate,
    LineItemType,
    PurchaseRule,
    PurchaseRuleLink,
    Seller,
    TermsTemplate,
    TermsTemplateFee,
    TermsType,
} from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { useCollection } from "../../../hooks/useCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { getAdvancedPurchaseRulePayload } from "../../../models/TermsTemplatePurchaseRule";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import {
    getAdvancedPurchaseRuleLinkPayload,
    getBasicPurchaseRuleLinkPayload,
    getPurchaseRulePayload,
    useFormValues,
    useLineItemTemplate,
} from "./TermsTemplateFormValues";
import { TermTemplateForm } from "./TermTemplateForm";

interface EditTermsTemplateProps {
    termsTemplate?: PurchaseRuleLink;
    showAgentFormOptions?: boolean;
    onSuccess?: (termsTemplate: PurchaseRuleLink) => void;
}

interface SavedPurchaseRule extends PurchaseRule {
    id: ID;
}

export const EditTermsTemplateForm = ({ termsTemplate, showAgentFormOptions, onSuccess }: EditTermsTemplateProps) => {
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const [isSaveAsNew, setIsSaveAsNew] = useState(false);

    const purchaseRuleIds = termsTemplate?.purchaseRules.map((purchaseRule) => {
        return purchaseRule.purchaseRulePermanentId;
    });

    const { data: agents } = useAutoFetchCollection<Affiliate>(["affiliates"]);

    const linkedAgents = termsTemplate?.entities
        .map((entity) => agents.find((agent) => agent.id === entity.id))
        .filter(Boolean);

    const { data: termsTemplatePurchaseRules } = useCollection<PurchaseRule>(
        "purchaseRules",
        { "id[in]": purchaseRuleIds?.join(",") },
        { enabled: !!purchaseRuleIds, staleTime: 3000, keepPreviousData: true },
    );

    const values = useFormValues(termsTemplatePurchaseRules, termsTemplate);

    // Affiliate discount template
    const { data: discountTemplate, isLoading: isLoadingDiscountTemplate } = useLineItemTemplate(
        LineItemType.AFFILIATE_DISCOUNT,
    );

    // Affiliate commission template
    const { data: commissionTemplate, isLoading: isLoadingCommissionTemplate } = useLineItemTemplate(
        LineItemType.AFFILIATE_COMMISSION,
    );

    // Save terms template purchase rule link
    const { mutateAsync: savePurchaseRuleLink, isLoading: isSavingPurchaseRuleLink } = useMutation(
        async (payload: PurchaseRuleLink) => {
            return termsTemplate?.id && !isSaveAsNew
                ? api.put<PurchaseRuleLink>(`/purchaseRuleLinks/${termsTemplate.id}`, payload)
                : api.post<PurchaseRuleLink>("/purchaseRuleLinks", payload);
        },
    );

    // Save terms template purchase rule (discount or commission)
    const { mutateAsync: savePurchaseRule, isLoading: isSavingPurchaseRule } = useMutation(
        async (purchaseRule: PurchaseRule) => {
            return purchaseRule?.id
                ? api.put<PurchaseRule>(`/purchaseRules/${purchaseRule.id}`, purchaseRule)
                : api.post<PurchaseRule>("/purchaseRules", purchaseRule);
        },
    );

    const createAndSavePurchaseRule = async (
        products: ID[],
        value: TermsTemplateFee,
        template: LineItemTemplate,
        seller: Seller,
    ) => {
        const payload = getPurchaseRulePayload({ all: false, selected: products }, value, seller, template, true);
        return savePurchaseRule(payload);
    };

    const processProductTermExceptions = async (
        values: TermsTemplate,
        seller: Seller,
        discountTemplate: LineItemTemplate,
        commissionTemplate: LineItemTemplate,
    ) => {
        // this variable contains an array of promises. [promise, promise ...]
        const termExceptionPurchaseRulePromises = values.productTermExceptions.flatMap((productTermException) => [
            // Create a promise for discount purchase rule
            createAndSavePurchaseRule(
                productTermException.products,
                productTermException.discount,
                discountTemplate,
                seller,
            )
                .then((result) => ({ type: LineItemType.AFFILIATE_DISCOUNT_OVERRIDE, data: result.data }))
                .catch((error) => ({ type: LineItemType.AFFILIATE_DISCOUNT_OVERRIDE, error })),
            // Create a promise for commission purchase rule
            createAndSavePurchaseRule(
                productTermException.products,
                productTermException.commission,
                commissionTemplate,
                seller,
            )
                .then((result) => ({ type: LineItemType.AFFILIATE_COMMISSION_OVERRIDE, data: result.data }))
                .catch((error) => ({ type: LineItemType.AFFILIATE_COMMISSION_OVERRIDE, error })),
        ]);

        // makes api call to create purchase rules for discounts and commissions
        const results = await Promise.all(termExceptionPurchaseRulePromises);
        const discountRules: PurchaseRule[] = [];
        const commissionRules: PurchaseRule[] = [];
        let hasError = false;

        for (const result of results) {
            if ("error" in result) {
                console.error(`Error processing ${result.type}:`, result.error);
                hasError = true;
            } else if (result.type === LineItemType.AFFILIATE_DISCOUNT_OVERRIDE) {
                discountRules.push(result.data);
            } else if (result.type === LineItemType.AFFILIATE_COMMISSION_OVERRIDE) {
                commissionRules.push(result.data);
            }
        }

        if (hasError) {
            flash.danger("Error while saving the changes.");
        }

        return {
            discountRules,
            commissionRules,
        };
    };

    const handleBasicTermsFormSubmit = async (values: TermsTemplate) => {
        const { data: savedDiscount } = await savePurchaseRule(
            getPurchaseRulePayload(values.products, values.discount, seller, discountTemplate!),
        );

        const { data: savedCommission } = await savePurchaseRule(
            getPurchaseRulePayload(values.products, values.commission, seller, commissionTemplate!),
        );

        // creates 2 purchase rules per product term exception, 1 for discount and 1 for commission.
        const {
            discountRules: savedProductTermDiscountExceptions,
            commissionRules: savedProductTermCommissionExceptions,
        } = await processProductTermExceptions(values, seller, discountTemplate!, commissionTemplate!);

        const purchaseRuleLinkPayload = getBasicPurchaseRuleLinkPayload({
            values,
            seller,
            discountPurchaseRule: savedDiscount,
            commissionPurchaseRule: savedCommission,
            productTermExceptionDiscountPurchaseRules: savedProductTermDiscountExceptions,
            productTermExceptionCommissionPurchaseRules: savedProductTermCommissionExceptions,
            termsTemplate: isSaveAsNew ? undefined : termsTemplate,
        });

        const { data: savedPurchaseRuleLink } = await savePurchaseRuleLink(purchaseRuleLinkPayload);

        invalidate("purchaseRules", savedDiscount.id);
        invalidate("purchaseRules", savedCommission.id);

        return savedPurchaseRuleLink;
    };

    const handleAdvancedTermsFormSubmit = async (values: TermsTemplate) => {
        const adjustmentPurchaseRulePromises = [];
        const commissionPurchaseRulePromises = [];
        for (const partnerDemographicTerm of values.partnerDemographicTerms) {
            const adjustmentPayload = getAdvancedPurchaseRulePayload(
                seller,
                discountTemplate!,
                partnerDemographicTerm,
                termsTemplatePurchaseRules,
                termsTemplate,
            );
            // get all adjustment PurchaseRule promises
            adjustmentPurchaseRulePromises.push(savePurchaseRule(adjustmentPayload));
            const commissionPayload = getAdvancedPurchaseRulePayload(
                seller,
                commissionTemplate!,
                partnerDemographicTerm,
                termsTemplatePurchaseRules,
                termsTemplate,
            );
            // get all commission PurchaseRule promises
            commissionPurchaseRulePromises.push(savePurchaseRule(commissionPayload));
        }

        // resolve and get all saved ids
        const savedAdjustmentPurchaseRulesResult = await Promise.all(adjustmentPurchaseRulePromises);
        const savedAdjusmentIds = savedAdjustmentPurchaseRulesResult.map(
            (item) => (item as AxiosResponse<SavedPurchaseRule>).data.id,
        );

        const savedCommissionPurchaseRulesResult = await Promise.all(commissionPurchaseRulePromises);
        const savedCommissionIds = savedCommissionPurchaseRulesResult.map(
            (item) => (item as AxiosResponse<SavedPurchaseRule>).data.id,
        );

        // save purchase rule link
        const purchaseRuleLinkPayload = getAdvancedPurchaseRuleLinkPayload(
            values,
            seller,
            savedAdjusmentIds,
            savedCommissionIds,
            isSaveAsNew ? undefined : termsTemplate,
        );

        const { data: savedPurchaseRuleLink } = await savePurchaseRuleLink(purchaseRuleLinkPayload);

        invalidate("purchaseRules");

        return savedPurchaseRuleLink;
    };

    const handleFormSubmit = async (values: TermsTemplate) => {
        const savedPurchaseRuleLink = await (values.termsType === TermsType.ADVANCED
            ? handleAdvancedTermsFormSubmit(values)
            : handleBasicTermsFormSubmit(values));

        invalidate("purchaseRuleLinks");

        if (showAgentFormOptions) {
            onSuccess?.(savedPurchaseRuleLink);
        } else {
            history.push("/distribution/termsTemplates");
        }

        if (termsTemplate) {
            flash.success("Terms template updated");
        } else {
            flash.success("Terms template created");
        }
    };

    if (isLoadingDiscountTemplate || isLoadingCommissionTemplate) {
        return <Loader />;
    }

    if (!discountTemplate || !commissionTemplate) {
        return (
            <ContentLayout isWide>
                <Alert color="warning">
                    There are no commission or discount agent templates defined. Please contact support.
                </Alert>
            </ContentLayout>
        );
    }

    if (!values) {
        return <Loader />;
    }

    const isSaving = isSavingPurchaseRule || isSavingPurchaseRuleLink;

    return (
        <TermTemplateForm
            values={values}
            isSaving={isSaving}
            showAgentFormOptions={showAgentFormOptions}
            termsTemplate={termsTemplate}
            linkedAgents={linkedAgents}
            handleFormSubmit={handleFormSubmit}
            setIsSaveAsNew={setIsSaveAsNew}
        />
    );
};
